import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  map,
  switchMap,
  mergeMap,
  filter,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  selectAppStateUser,
  selectAppStateSelectedCustomer,
} from '@app/ngrx-customer/store';
import { CART_ACTIONS, CART_SELECTORS } from '@usf/ngrx-order';
import {
  ClientData,
  MarketingConversionPayload,
  MarketingService,
  MetaCookieData,
} from './marketing.service';
import { CookieService } from '@shared/services/cookie/cookie.service';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class MarketingConversionEffects {
  private readonly userState$ = this.store$.select(selectAppStateUser);
  private readonly selectedCustomerState$ = this.store$.select(
    selectAppStateSelectedCustomer,
  );
  private readonly cart$ = this.store$.select(CART_SELECTORS.selectCart);
  private readonly isNative = Capacitor.isNativePlatform();

  orderPlacedEffect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CART_ACTIONS.submitCartSuccess),
        switchMap(() => {
          const clientData = this.buildClientData();
          if (clientData === undefined) {
            return EMPTY;
          }
          return this.cart$.pipe(
            filter(cart => cart?.orderHeader?.orderId != null),
            take(1),
            withLatestFrom(this.userState$, this.selectedCustomerState$),
            map(([cart, user, customer]) => ({
              marketingConversion: {
                event: 'ORDER_PLACED',
                userData: {
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  email: user?.email,
                },
                customerData: {
                  city: customer?.city,
                  state: customer?.state,
                  zip: customer?.zip,
                },
                clientData,
                eventData: {
                  orderValue: cart?.orderHeader?.totalPrice ?? 0,
                  caseCount: cart?.tallies?.totalCases ?? 0,
                },
              },
            })),
            mergeMap((payload: MarketingConversionPayload) =>
              this.marketingService.sendLinkConversionEvent(payload),
            ),
          );
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store,
    private readonly marketingService: MarketingService,
    private readonly cookieService: CookieService,
  ) {}

  /**
   * Builds the client data object from cookies, or returns undefined if not present.
   */
  private buildClientData(): ClientData | undefined {
    const fbc = this.isNative
      ? localStorage.getItem('_fbc')
      : this.cookieService.getCookieValue('_fbc');
    const fbp = this.cookieService.getCookieValue('_fbp');
    if (fbc === null && fbp === null) {
      return undefined;
    }
    const metaData: MetaCookieData = {};
    if (fbc !== null) metaData.fbc = fbc;
    if (fbp !== null) metaData.fbp = fbp;
    return { meta: metaData };
  }
}
